import { useMemo } from 'react';

import { EColorVariablesV2, hex, opacity, PANEL_THEME_COLOR_CONTENT } from '@/types/theme-packs.types';
import useGetWorkingProjectThemeMode from './use-get-working-project-theme-mode';
import useGetWorkingProjectThemePack from './use-get-working-project-theme-pack';
import { useGetColorOptionsFromColor } from './colors/use-get-color-options-from-color';

const useThemeColors = () => {
  const themeMode = useGetWorkingProjectThemeMode();
  const themePack = useGetWorkingProjectThemePack();
  const themeColors = useGetColorOptionsFromColor(themePack, themeMode);
  return { themeColors, themeMode, themePack };
};

type ReturnProps = {
  [key in EColorVariablesV2]?: [hex, opacity];
};

const cache = new Map<string, ReturnProps>();

const useWorkingProjectColorsByV2Names = (): ReturnProps => {
  const { themeColors, themePack, themeMode } = useThemeColors();
  const cacheKey = `${themeMode}-${themePack?.color_pack}-${themePack?.updated_at}`;

  return useMemo(() => {
    if (cacheKey && cache.has(cacheKey)) {
      const cachedResults = cache.get(cacheKey);
      if (cachedResults) {
        return cachedResults;
      }
    }

    const colorsByV2Names = Object.fromEntries(
      PANEL_THEME_COLOR_CONTENT.map((meta) => {
        const color = themeColors?.[meta?.mappedV1Value];
        return [meta.id, color ? [color[0], color[1]] : undefined];
      })
    );

    if (cacheKey) {
      // if size is greater than 100, clear the cache
      if (cache.size > 100) {
        cache.clear();
      }
      // set the cache with the key and colors
      cache.set(cacheKey, colorsByV2Names);
    }
    return colorsByV2Names;

    // since the values of themeColors never change, because this app doesn't have any way of
    // editing channel theme packs, we don't need to add them in the dependency array
    // we only care about the cacheKey changing - which is a combination of the themeMode and themePack id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);
};

export default useWorkingProjectColorsByV2Names;
